import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { User } from "@repo/types";
import type { Rsp } from "@repo/types";
import { UserAPI } from "@repo/api/user";
import { Img } from "react-image";
import { GetQuality } from "@/pages/user/badges/-components";
import dayjs from "dayjs";
import { QuestionFill } from "@/components/icons/svg.tsx";
import BaseModal from "@/components/modal/base-modal";
import { useDocumentTitle } from "@/hooks/useDocumentTitle.ts";

export const Route = createFileRoute("/user/badges/$id")({
  component: BadgeDetailPage,
});


export default function BadgeDetailPage() {
  const params = useParams({ from: "/user/badges/$id" });
  const [badge, setBadge] = useState<User.Badge>({
    id: "0",
    name: "加载中",
    image: "",
    unlock_at: 0,
    allow_upgrade: false,
    author: "",
    description: "",
    level: 0,
    max_level: 0,
    permanent: false,
    status: 0,
    unlock_condition: "",
    collectable: false,
    quality: 0,
    effect: "",
  });
  const [error, setError] = useState<Rsp<null>>({
    status: 0,
    msg: "",
    data: null,
    request_id: "",
    timestamp: 0,
  });

  useDocumentTitle(badge.name || "我的徽章");

  useEffect(() => {
    UserAPI.getBadgeDetail(params.id)
      .then(
        ({ data }) => setBadge(data),
        (err: Rsp<null>) => setError(err),
      );
  }, [params.id]);

  // if (loading) {
  //   return (
  //     <div className={"flex items-center justify-center min-h-screen"}>
  //       <Spinner size={"md"} color={"primary"}/>
  //     </div>)
  // }

  if (error.status !== 0) {
    return (
      <div className={"flex min-h-screen flex-col items-center justify-center opacity-40"}>
        <Img alt={"empty"} src={"/img/faill@1.0x.png"} width={120} height={120} />
        <div className={"text-sm"}>哎呀！你遇到了一个错误</div>
        <div className={"text-sm"}>
          请求ID：{error.request_id || "ALPHA_FURRIES-0000.0000.0001"}
        </div>
        <div className={"text-sm"}>错误讯息：{error.msg}</div>
      </div>
    );
  }
  return (
    <div
      className={"flex min-h-screen flex-col items-center justify-center bg-white dark:bg-gray-800"}
    >
      <Img src={badge.image} width={168} height={168} alt={badge.name} className={"-mt-[180px]"} />
      <div className={"mx-4 flex flex-col items-center gap-4"}>
        <div className={"text-3xl font-bold"}>{badge.name}</div>
        <div className={"flex flex-row gap-2 text-sm"}>
          <div>Lv.{badge.level}</div>·
          <div>
            {badge.permanent ? (
              <span className={"text-orange-500"}>永久</span>
            ) : (
              <span className={"text-gray-500"}>非永久</span>
            )}
          </div>
          ·
          <div>
            <span
              style={{
                color: GetQuality(badge.quality).color,
              }}
            >
              {GetQuality(badge.quality).text}
            </span>
          </div>
          ·
          <div>
            {badge.allow_upgrade ? (
              <span className={"text-blue-500"}>可升级</span>
            ) : (
              <span className={"text-gray-400"}>不可升级</span>
            )}
          </div>
        </div>
        {badge.effect === "hidden" && <HiddenEffect />}
        <div className={"flex h-8 w-[75%] flex-row items-center gap-4"}>
          <span className={"block h-[1px] flex-1 bg-gray-300"} />
          <div className={"h-2 w-2 shrink-0 rotate-45 bg-gray-400"}></div>
          <span className={"block h-[1px] flex-1 bg-gray-300"} />
        </div>
        <div className={"mx-8 text-sm text-gray-800"}>{badge.description}</div>
        <div className={"mx-8 text-sm text-gray-400"}>解锁条件：{badge.unlock_condition}</div>
      </div>

      <div className={"absolute bottom-24 mx-8 flex flex-col gap-2 text-sm text-gray-800"}>
        <div className={"text-center"}>
          解锁时间：{dayjs.unix(badge.unlock_at).format("YYYY-MM-DD HH:mm:ss")}
        </div>
      </div>
    </div>
  );
}

function HiddenEffect() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className={"flex flex-row items-center gap-1 rounded-md bg-gray-100 px-4 py-3 text-sm"}
        onClick={() => setOpen(true)}
      >
        <QuestionFill width={16} height={16} fill={"gray"} />
        这个徽章有一个隐藏效果
      </div>
      <BaseModal
        open={open}
        showHeader
        title="徽章隐藏效果"
        showCancel={false}
        onConfirm={() => setOpen(false)}
        onClose={() => setOpen(false)}
      >
        <div className={"flex flex-col gap-2 text-gray-800"}>
          <div>隐藏效果代表着徽章在未来会有一个神秘奖励。</div>
          <div>
            这个奖励的价值取决于徽章的等级和稀有程度。其中，徽章等级最高为
            <span className={"text-red-500"}>5级</span>，稀有度最高为
            <span className={"text-orange-500"}>限量</span>
          </div>
          <div>奖励会随时开启，记得常来看看哦~</div>
        </div>
      </BaseModal>
    </>
  );
}
